<script lang="ts">
	import type { BoundaryCoordinates } from '$lib/components/map/types';
	import type { MainPoiCategory, PoiData } from '$lib/components/pois/types';
	import type { FooterLinks, SEOData } from '$lib/components/seo/types';
	import type { PoisStoreContent } from '../../../../types';
	import type { PageData } from './$types';
	import { page } from '$app/stores';
	import Button from '$lib/components/atoms/button/Button.svelte';
	import Footer from '$lib/components/footer/Footer.svelte';
	import { getMapLayoutContext } from '$lib/components/map/map-layout-context';
	import DatasheetPanel from '$lib/components/pois/datasheet/DatasheetPanel.svelte';
	import { isDatasheetPoiData } from '$lib/components/pois/utils';
	import { track } from '$lib/services/tracking/googleAnalytics';
	import { getSearchParamsStore, gotoRelativeUrl, gotoWithParams } from '$lib/services/urls/searchParams';
	import { isVanitySearchParams } from '$lib/services/urls/vanity';
	import { sessionPoiListCanonicalLink } from '$lib/utils/sessionStorage';
	import * as m from '$paraglide/messages';
	import { onMount } from 'svelte';
	import { type Readable } from 'svelte/store';

	export let data: PageData;
	const searchParams = getSearchParamsStore();

	let poisStore: Readable<PoisStoreContent>;
	let selectedPoiData: PoiData | undefined;
	let mapBounds: BoundaryCoordinates | undefined;
	let poiCategories: MainPoiCategory[];
	let seoDataStore: Readable<SEOData | undefined>;
	let partnersLinks: FooterLinks[];

	function handleReactivityOnPageData(data: PageData) {
		seoDataStore = data.seoDataStore;
		partnersLinks = data.partnersLinks;
		poisStore = data.poisStore;
		selectedPoiData = $poisStore?.selectedPoiData;
		mapBounds = $poisStore?.mapBounds;
		poiCategories = $poisStore?.poiCategories;
	}

	$: handleReactivityOnPageData(data);
	$: seo = $seoDataStore;

	const { moveDrawer, onAccommodationSelectDates, onAccommodationClearDates } = getMapLayoutContext();

	function closePoiDatasheet() {
		track({
			action: 'datasheet_close',
			payload: { action: 'click' },
		});

		const relativePoilistUrlFromSession = sessionPoiListCanonicalLink?.consume?.();

		// isVanitySearchParams to handle case where user select dates on poi datasheet to keep dates
		if (relativePoilistUrlFromSession && isVanitySearchParams($page.url)) {
			gotoRelativeUrl(relativePoilistUrlFromSession);
		} else {
			gotoWithParams('/map', {
				...$searchParams,
				bounds: mapBounds,
				poiCategories,
				selectedPoi: undefined,
			});
		}
	}

	onMount(() => {
		// 272px is the height of the datasheet when the drawer is partially open
		setTimeout(() => moveDrawer(272), 0);
	});
</script>

<!-- TODO: Replace this if statement with a proper route guard in page.ts -->
{#if selectedPoiData && isDatasheetPoiData(selectedPoiData)}
	<div class="fixed top-2 left-2 md:hidden">
		<Button
			variant="filled"
			color="white"
			size="md"
			icon="arrow-left"
			iconClasses="text-xl"
			ariaLabel={m.poi_datasheet__back_to_list()}
			onclick={closePoiDatasheet}
		/>
	</div>
	<DatasheetPanel
		poi={selectedPoiData}
		on:selectDates={({ detail }) => {
			onAccommodationSelectDates(detail);
		}}
		on:clearDates={() => {
			onAccommodationClearDates();
		}}
		on:close={closePoiDatasheet} />
	<Footer class="lt-md:pb-20 relative" {seo} {partnersLinks} />
	<!-- TODO: pb-20 needed to rise above floating bar on mobile, remove after refactor -->
{/if}
